import { AxiosInstance } from 'axios'
import { Alpha3Code } from 'i18n-iso-countries'

import { FraudCheckResult, FraudCheckType } from '../agentVerification/agentVerification.types'
import { RoleCategory } from '../admin/admin.types'

import { Eligibility, Monitoring, OrganisationTypes, SubTag, VettingEventScope } from '@percent/domain/verifications'
import {
  RelationshipValues,
  RelationshipTypeValues
} from '@percent/admin-dashboard/app/validations/nonprofitDetail/relatedOrganisations/RelatedOrganisations.types'

export type ValidationSubmissionStatus = 'pending' | 'approved' | 'rejected'

export type ValidationSubmissionsCapabilities = {
  percentClient: AxiosInstance
}

export type GetValidationSubmissionProps = {
  page?: number
  pageSize?: number
  cursor?: string | null
  query?: string | null
  statuses?: (ValidationSubmissionStatus | null)[]
  sort?: string | null
  direction?: string | null
  organisationId?: string | null
}

export type ValidationSubmission = {
  id: string
  createdAt: string
  status: string
  partner: {
    id: string
    name: string
  }
  configuration: {
    id: string
    name: string
  }
  agentVerification: {
    email: string
  }
  organisation: {
    id: string
    name: string
    countryCode: Alpha3Code
  }
  validationRequest: Partial<{
    id: string
    name: string
    countryCode: Alpha3Code
  }>
}

export type GetHeldValidationSubmissionsProps = {
  pageSize?: number
  cursor?: string | null
  query?: string | null
  partnerId?: string | null
  configurationIds?: (string | null)[] | null
}

export type HeldValidationSubmission = {
  id: string
  receivedAt: string
  heldAt: string | null
  assessment: {
    verdict: 'clear' | 'suspected' | 'wave' | 'error'
    assessor: string
    evidence: Record<string, unknown>
    assessedAt: string
  } | null
  partner: { id: string; name: string | null }
  configuration: { id: string; name: string | null }
  submission: {
    firstName: string | null
    lastName: string | null
    email: string | null
    countryCode: Alpha3Code | null
    status: string
  }
}

export type BulkReleaseHoldsResult = {
  released: string[]
  notHeld: string[]
  notFound: string[]
}

export type ValidationSubmissionLogType = ValidationSubmissionLog['type']

export type ValidationSubmissionEligibilityResultType = {
  updatedCheck?: {
    check: 'eligibility' | 'monitoring_subscription'
    result: string | null
  } & Partial<Pick<Eligibility, 'results'>>
}

export type ValidationSubmissionMonitoringResultType = {
  updatedCheck?: {
    check: 'eligibility' | 'monitoring_subscription'
    result: string | null
  } & Partial<Pick<Monitoring, 'results'>>
}

export type CountryComplianceRegulationResult = {
  regulation: string
  outcome: string
  reasonCode: string
  verifiedAt: string | null
}

export type CountryComplianceResults = {
  regulations?: CountryComplianceRegulationResult[]
}

export type ValidationSubmissionCountryComplianceResultType = {
  updatedCheck?: {
    check: 'country_compliance'
    result: string | null
    results?: CountryComplianceResults | null
  }
}

export type ValidationSubmissionStatusDataType = {
  updatedCheck?:
    | {
        check: 'agent_verification'
        status: 'approved'
        approvalReasonCode?: string
      }
    | {
        check: 'agent_verification'
        status: 'rejected'
        rejectionReasonCode?: string
      }
}

export type EmptyDataValidationSubmissionLogType =
  | 'validation_request_accepted'
  | 'validation_request_rejected'
  | 'validation_submission_processed'
  | 'validation_submission_pending'
  | 'validation_submission_failed_outcome_email_sent'
  | 'validation_submission_succeeded'
  | 'validation_submission_succeeded_outcome_email_sent'
  | 'fraud_check_application_check_completed'
  | 'fraud_check_advanced_domain_check_completed'
  | 'fraud_check_basic_domain_check_completed'
  | 'fraud_check_application_check_requested'
  | 'fraud_check_advanced_domain_check_requested'
  | 'fraud_check_basic_domain_check_requested'
  | 'fraud_check_document_check_completed'
  | 'fraud_check_document_check_requested'
  | 'fraud_check_requested'
  | 'validation_request_accepted_by_auto_merge'

export type ValidationSubmissionFailureReason =
  | { check: 'validation_request'; reason: { rejectionReasonCode: string | null } }
  | { check: 'agent_verification'; reason: { rejectionReasonCode: string | null } }
  | {
      check: 'eligibility'
      reason: {
        status: string
        results: {
          eligibilityStatus: string
          confirmedActivitySubTags: { id: string; name: string }[] | null
          rejectedActivitySubTags: { id: string; name: string }[] | null
        }
      }
    }
  | { check: 'compliance'; reason: { results: NonNullable<Monitoring['results']> } }
  | { check: 'country_compliance'; reason: { status: string; results: CountryComplianceResults | null } }

export type ValidationSubmissionFailedDataType = {
  // failedChecks mirrors failureReasons but without detailed reason data — not used for rendering
  failedChecks: string[]
  failureReasons: ValidationSubmissionFailureReason[]
} | null

export type ValidationSubmissionLog =
  | {
      id: string
      validationSubmissionId: string
      type: 'eligibility_result_change'
      data: ValidationSubmissionEligibilityResultType
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'monitoring_subscription_result_change'
      data: ValidationSubmissionMonitoringResultType
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'agent_verification_status_change'
      data: ValidationSubmissionStatusDataType
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'country_compliance_check_result_change'
      data: ValidationSubmissionCountryComplianceResultType
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'validation_submission_failed'
      data: ValidationSubmissionFailedDataType
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: EmptyDataValidationSubmissionLogType
      data: {
        account: string
        accountEmail: string
      } | null
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'fraud_check_completed'
      data: { fraudCheckResult: FraudCheckResult }
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'fraud_check_marked_as_safe'
      data: {
        account: string
        accountEmail: string
        reusedFromValidationSubmissionId?: string
      } | null
      createdAt: Date
    }
  | {
      id: string
      validationSubmissionId: string
      type: 'fraud_review_ralph_recommendation_received'
      data: {
        ralphFraudReviewDecision: string
        ralphFraudReviewReasoning: string
        ralphFraudReviewConfidence: string
      } | null
      createdAt: Date
    }

export type GetValidationSubmissionConfigurationsProps = {
  pageSize?: number
  cursor?: string | null
  direction?: 'ASC' | 'DESC'
  partnerId: string
}

export const PROGRAM_PURPOSE_OPTIONS = [
  'individual_apis',
  'single_api',
  'hosted_validation_form',
  'static_link',
  'validation_invite',
  'token_service_method',
  'workplace_giving',
  'grants',
  'product_nomination',
  'monetary_donation'
] as const

export type ProgramPurpose = typeof PROGRAM_PURPOSE_OPTIONS[number]

export interface ValidationSubmissionConfiguration {
  id: string
  name: string
  partnerId: string
  configuration?: {
    checks?: Array<
      AgentVerificationCheck | EligibilityCheck | ComplianceCheck | ValidationRequestCheck | AutoFraudCheck
    >
  }
  createdAt: string
  lastUsedAt?: string | null
  deprecatedAt?: string | null
  purposes?: ProgramPurpose[] | null
}

export type AgentVerificationAssociationCheckType =
  | 'manual'
  | 'association_emails'
  | 'domain_matching'
  | 'trusted_school_domain'
  | 'external_agent_verification'
  | 'automatic_document_review'
  | 'defer_to_partner'

export type AgentVerificationIdentityCheckType = 'inbox_verification' | 'kyc_verification' | 'otp_code'

export type RoleCategoriesConfig = {
  accepted: RoleCategory[]
  rejected: RoleCategory[]
}

export type DetailsMatchingStep = {
  mode?: string
  roleCategories?: {
    accepted?: RoleCategory[] | null
    rejected?: RoleCategory[] | null
  } | null
}

export type AgentVerificationCheck = {
  type: 'agent_verification'
  identityChecks?: Array<{
    type: AgentVerificationIdentityCheckType
  }>
  isStatusDynamic: boolean
  associationChecks?: Array<{
    type: AgentVerificationAssociationCheckType
    steps?: {
      detailsMatching?: DetailsMatchingStep
    }
  }>
}

// How a programme diverges from the default set of each phase; an absent group, or `{}`, means the
// default set unmodified, so an empty object is data rather than an omission.
export type FraudCheckGroupConfig = {
  optInChecks?: string[]
  disabledDefaultChecks?: string[]
}

export type FraudChecksByGroup = {
  basicDomainChecks?: FraudCheckGroupConfig
  advancedDomainChecks?: FraudCheckGroupConfig
  applicationChecks?: FraudCheckGroupConfig
  documentChecks?: FraudCheckGroupConfig
  testChecks?: FraudCheckGroupConfig
}

export type FraudCheckGroupKey = keyof FraudChecksByGroup

export type AutoFraudCheck = {
  type: 'fraud_check'
  checkGroups?: FraudChecksByGroup
  /** @deprecated Replaced by `checkGroups`; only configurations written before it carry a list. */
  checks?: { type: FraudCheckType; collection?: { key: string }[] }[]
  weightThresholds?: { needsReview?: number; autoReject?: number }
  addedAt?: string
}

export type ExceptionRule = {
  name?: string
  event: {
    tags: string[]
    action: 'add-tags' | 'remove-tags' | 'replace-tags'
  }
  conditions: {
    fact: 'country' | 'organisationTypes'
    value: string
    operator: 'equal' | 'contains'
  }
}

export type EligibilityCheck = {
  type: 'eligibility'
  rules?: ExceptionRule[]
}

export type ComplianceCheck = {
  type: 'compliance'
  flags: VettingEventScope[]
  failingFlags: VettingEventScope[]
}
export type CountryComplianceCheck = {
  type: 'country_compliance'
  requiredRegulations: string[]
}
export type ValidationRequestCheck = {
  type: 'validation_request'
}
export type ActicityCheck = {
  type: 'activity_check'
}

export type FieldType = 'short_text' | 'multiple_choice' | 'number' | 'select' | 'website'

export type FormField = {
  title: string
  id: string
  placeholder?: string
  type: FieldType
  validation?: {
    maxLength?: number
    required?: boolean
  }
  options?: {
    key: string
    value: string
  }[]
}
export interface ValidationSubmissionConfigurationResponse {
  id: string
  name: string | null
  partnerId: string
  primaryColor: string | null
  terms: string | null
  logo: string | null
  shouldSendOutcomeEmail: boolean
  acceptanceEmailBody: string | null
  rejectionEmailBody: string | null
  partnerFields: FormField[]
  configuration: {
    checks: Array<
      | AgentVerificationCheck
      | EligibilityCheck
      | ComplianceCheck
      | CountryComplianceCheck
      | ValidationRequestCheck
      | ActicityCheck
      | AutoFraudCheck
    >
    isStatusDynamic: boolean
    captchaMandatory?: boolean | null
    allowedOrganisations: {
      types: OrganisationTypes[]
    }
    disallowedOrganisations: {
      relationships: { relationship: RelationshipValues; type: RelationshipTypeValues }[] // Define specific type if needed
    }
  }
  programWebsite: string | null
  allowedActivitySubTags: Omit<SubTag, 'deprecatedAt'>[]
  activitySubTags: Array<{
    id: string
    configurationId: string
    activitySubTagId: string
    type: 'allow'
    createdAt: string
    deletedAt: string | null
  }>
}

export interface CreateValidationSubmissionHostedConfiguration {
  name?: string | null
  primaryColor?: string | null
  partnerId: string
  terms?: string | null
  shouldSendOutcomeEmail?: boolean
  acceptanceEmailBody?: string | null
  rejectionEmailBody?: string | null
  programWebsite?: string | null
  partnerFields?: FormField[]
}

export interface PatchValidationSubmissionHostedConfiguration {
  id: string
  name?: string | null
  primaryColor?: string | null
  terms?: string | null
  shouldSendOutcomeEmail?: boolean
  acceptanceEmailBody?: string | null
  rejectionEmailBody?: string | null
  programWebsite?: string | null
  partnerFields?: FormField[] | null
  configuration: {
    allowedOrganisations: {
      types: OrganisationTypes[]
    }
    disallowedOrganisations: {
      relationships: DisallowedOrganisation[]
    }
    allowedReferers?: string[]
    checks: Array<AgentVerificationCheck | EligibilityCheck | ComplianceCheck | ValidationRequestCheck | AutoFraudCheck>
    redemption?: { id: string }
    isStatusDynamic: boolean
  }
}

export interface UpdateValidationSubmissionConfigurationStatus {
  id: string
  deprecated: boolean
}

export interface UpdateValidationSubmissionPurpose {
  id: string
  purposes: ProgramPurpose[]
}

export type DisallowedOrganisation = {
  organisationId?: string
  type: RelationshipTypeValues
  relationship: RelationshipValues
}

export type ActivityCheck = {
  type: 'activity_check'
}

export type AgentVerificationAssociationCheck = {
  type: AgentVerificationAssociationCheckType
}

export type AgentVerificationIdentityCheck = {
  type: AgentVerificationIdentityCheckType
}

export type UpdateValidationSubmissionSubTags = {
  id: string
  subtags: string[]
}
